import React, {useState, useEffect, useContext} from 'react';
import axios from '../../axiosConfig';
import { FiPlus, FiFileText, FiCheckCircle, FiClock, FiCalendar, FiRepeat, FiX } from 'react-icons/fi';
import '../../styles/DogReportsTab.css';
import AssignReportModal from './AssignReportModal';
import {TrainerContext} from "../../context/TrainerContext";
import {useToast} from "../commons/toast/ToastContext";

const DogReportsTab = ( {dogReports = { assignments: [], submissions: [] }, dogId} ) => {
    const { trainer } = useContext(TrainerContext);
    const toast = useToast();
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [finishDate, setFinishDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeSubmissionId, setActiveSubmissionId] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [localDogReports, setLocalDogReports] = useState(dogReports);

    // Fetch templates for assignment modal
    const fetchTemplates = async () => {
        try {
            const token = localStorage.getItem('jwt');
            const response = await axios.get(`/api/v1/reports/templates/school/${trainer.schoolId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTemplates(response.data);
        } catch (err) {
            console.error('Error fetching templates:', err);
            toast.error('Nie udało się pobrać szablonów', 'Błąd');
        }
    };

    // Fetch reports data
    const fetchReports = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('jwt');
            const response = await axios.get(`/api/v1/reports/dog/${dogId}/reports`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLocalDogReports(response.data);
        } catch (err) {
            console.error('Error fetching reports:', err);
            setError('Nie udało się pobrać raportów');
            toast.error('Nie udało się pobrać raportów', 'Błąd');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    // Update local state when prop changes
    useEffect(() => {
        setLocalDogReports(dogReports);
    }, [dogReports]);

    // Set today as default finish date when modal opens
    useEffect(() => {
        if (isFinishModalOpen) {
            const today = new Date();
            setFinishDate(today.toISOString().split('T')[0]);
        }
    }, [isFinishModalOpen]);

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'Nie określono';
        const date = new Date(dateString);
        return date.toLocaleDateString('pl-PL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Get recurrence label
    const getRecurrenceLabel = (recurrence) => {
        const recurrenceMap = {
            'ONCE': 'Jednorazowo',
            'DAILY': 'Codziennie',
            'EVERY_2_DAYS': 'Co 2 dni',
            'EVERY_3_DAYS': 'Co 3 dni',
            'EVERY_4_DAYS': 'Co 4 dni',
            'EVERY_5_DAYS': 'Co 5 dni',
            'EVERY_6_DAYS': 'Co 6 dni',
            'WEEKLY': 'Tygodniowo'
        };
        return recurrenceMap[recurrence] || recurrence;
    };

    // Handle report assignment creation
    const handleReportAssigned = async () => {
        // Instead of reloading the page, fetch the updated data
        await fetchReports();
        toast.success('Raport został pomyślnie przypisany', 'Sukces');
    };

    // Handle submission item click with proper event handling
    const handleSubmissionClick = (e, submissionId) => {
        // Toggle the active submission
        setActiveSubmissionId(activeSubmissionId === submissionId ? null : submissionId);
    };

    // Handle opening finish assignment modal
    const handleFinishAssignment = (assignment) => {
        setSelectedAssignment(assignment);
        setIsFinishModalOpen(true);
    };

    // Handle finishing assignment
    const handleSubmitFinishAssignment = async () => {
        if (!selectedAssignment || !finishDate) {
            toast.warning('Proszę wybrać datę zakończenia', 'Brakujące dane');
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('jwt');

            const requestData = {
                assignmentId: selectedAssignment.id,
                date: finishDate
            };

            await axios.post('/api/v1/reports/assignment/finish', requestData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Update the local state to reflect the changes
            setLocalDogReports(prevState => ({
                ...prevState,
                assignments: prevState.assignments.map(assignment =>
                    assignment.id === selectedAssignment.id
                        ? { ...assignment, endDate: finishDate }
                        : assignment
                )
            }));

            setIsFinishModalOpen(false);
            setSelectedAssignment(null);
            setFinishDate('');
            toast.success('Zadanie zostało pomyślnie zakończone', 'Sukces');
        } catch (err) {
            console.error('Error finishing assignment:', err);
            const errorMessage = err.response?.data?.message || 'Nie udało się zakończyć zadania';
            toast.error(errorMessage, 'Błąd');
        } finally {
            setLoading(false);
        }
    };

    // Function to generate dates based on recurrence pattern
    function generateDatesBasedOnRecurrence(startDate, endDate, recurrence) {
        const dates = [];
        let currentDate = new Date(startDate);

        // Loop until we reach or exceed the end date
        while (currentDate <= endDate) {
            dates.push(new Date(currentDate)); // Add a copy of the current date

            // Move to the next date based on recurrence pattern
            switch (recurrence) {
                case 'ONCE':
                    // For one-time reports, just add the start date
                    currentDate = new Date(endDate.getTime() + 86400000); // Jump past end date to exit loop
                    break;

                case 'DAILY':
                    currentDate.setDate(currentDate.getDate() + 1);
                    break;

                case 'EVERY_2_DAYS':
                    currentDate.setDate(currentDate.getDate() + 2);
                    break;

                case 'EVERY_3_DAYS':
                    currentDate.setDate(currentDate.getDate() + 3);
                    break;

                case 'EVERY_4_DAYS':
                    currentDate.setDate(currentDate.getDate() + 4);
                    break;

                case 'EVERY_5_DAYS':
                    currentDate.setDate(currentDate.getDate() + 5);
                    break;

                case 'EVERY_6_DAYS':
                    currentDate.setDate(currentDate.getDate() + 6);
                    break;

                case 'WEEKLY':
                    currentDate.setDate(currentDate.getDate() + 7);
                    break;

                default:
                    // Default to daily if recurrence pattern is unknown
                    currentDate.setDate(currentDate.getDate() + 1);
            }
        }

        return dates;
    }

    // Group submissions by assignment with complete date coverage
    const submissionsByAssignment = localDogReports.assignments.map(assignment => {
        // Get all submissions for this assignment
        let assignmentSubmissions = localDogReports.submissions.filter(
            submission => submission.assignmentId === assignment.id
        );

        // Create a Map keyed by date string to handle duplicates
        const submissionsByDate = new Map();

        // First, add all existing submissions to our map
        assignmentSubmissions.forEach(submission => {
            const dueDateStr = submission.dueDate ? new Date(submission.dueDate).toISOString().split('T')[0] : null;

            // If we don't have this date yet, or if this submission is completed and the existing one isn't,
            // use this submission
            if (!submissionsByDate.has(dueDateStr) ||
                (submission.completed && !submissionsByDate.get(dueDateStr).completed)) {
                submissionsByDate.set(dueDateStr, submission);
            }
        });

        // Now generate all expected dates based on recurrence pattern
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to beginning of today

        const startDate = new Date(assignment.startDate);
        startDate.setHours(0, 0, 0, 0); // Set to beginning of start date

        // Determine the end limit for generating submissions
        let endLimit = today;
        if (assignment.endDate) {
            const assignmentEndDate = new Date(assignment.endDate);
            assignmentEndDate.setHours(0, 0, 0, 0);
            // Use the earlier of today or assignment end date
            endLimit = assignmentEndDate < today ? assignmentEndDate : today;
        }

        // Only proceed if the assignment has started
        if (startDate <= endLimit) {
            // Generate all dates from start date to end limit based on recurrence
            const allExpectedDates = generateDatesBasedOnRecurrence(
                startDate,
                endLimit,
                assignment.recurrence
            );

            // Add any missing dates as pending reports
            allExpectedDates.forEach(date => {
                const dateStr = date.toISOString().split('T')[0];

                // If we don't already have a submission for this date, create one
                if (!submissionsByDate.has(dateStr)) {
                    // Create a placeholder submission
                    const pendingSubmission = {
                        id: `pending-${assignment.id}-${date.getTime()}`, // Generate a temporary ID
                        assignmentId: assignment.id,
                        submittedAt: null,
                        dueDate: date.toISOString(),
                        completed: false,
                        template: {
                            id: assignment.reportTemplateId,
                            title: assignment.reportTemplateTitle,
                            questions: [] // This will be populated when the user opens the form
                        },
                        answers: []
                    };

                    submissionsByDate.set(dateStr, pendingSubmission);
                }
            });
        }

        // Convert Map back to array
        const completeSubmissions = Array.from(submissionsByDate.values());

        // Sort submissions by due date
        completeSubmissions.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

        return {
            assignment,
            submissions: completeSubmissions
        };
    });

    if (loading && localDogReports.assignments.length === 0) return <div>Ładowanie raportów...</div>;
    if (error) return <div className="error">{error}</div>;

    return (
        <div className="dog-reports-tab">
            {submissionsByAssignment.length === 0 ? (
                <div className="dog-reports-header-container">
                    <button
                        className="report-create-btn dog-reports-top-btn"
                        onClick={(e) => {
                            e.preventDefault(); // Prevent default button behavior
                            setIsAssignModalOpen(true);
                        }}
                    >
                        <FiPlus className="reports-btn-icon" />
                        <span>Przypisz Raport</span>
                    </button>
                </div>
            ) : (
                /* Only show button when reports exist - no header section */
                <div className="dog-reports-header-container">
                    <button
                        className="report-create-btn"
                        onClick={(e) => {
                            e.preventDefault(); // Prevent default button behavior
                            setIsAssignModalOpen(true);
                        }}
                    >
                        <FiPlus className="reports-btn-icon" />
                        <span>Przypisz Raport</span>
                    </button>
                </div>
            )}

            {submissionsByAssignment.length === 0 ? (
                <div className="reports-empty-state">
                    <FiFileText className="reports-empty-icon" />
                    <p>Brak przypisanych raportów</p>
                    <p className="reports-empty-subtitle">Przypisz raport, aby właściciel mógł go wypełniać</p>
                    <button
                        className="report-create-btn"
                        onClick={(e) => {
                            e.preventDefault(); // Prevent default button behavior
                            setIsAssignModalOpen(true);
                        }}
                    >
                        <FiPlus className="reports-btn-icon" />
                        <span>Przypisz Raport</span>
                    </button>
                </div>
            ) : (
                <div className="dog-reports-assignments-list">
                    {submissionsByAssignment.map(({ assignment, submissions }) => {
                        // Check if assignment has ended
                        const assignmentEndDate = assignment.endDate ? new Date(assignment.endDate) : null;
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        let assignmentHasEnded = false;

                        if (assignmentEndDate) {
                            assignmentEndDate.setHours(23, 59, 59, 999);
                            assignmentHasEnded = today > assignmentEndDate;
                        }

                        return (
                            <div key={assignment.id} className="dog-reports-assignment-section">
                                <div className="dog-reports-assignment-header">
                                    <div className="dog-reports-assignment-info">
                                        <h3 className="dog-reports-assignment-template">{assignment.reportTemplateTitle}</h3>
                                        <div className="dog-reports-assignment-meta">
                                            <span className="dog-reports-recurrence-badge">
                                                <FiRepeat className="dog-reports-meta-icon" />
                                                {getRecurrenceLabel(assignment.recurrence)}
                                            </span>
                                            <span className="dog-reports-date-badge">
                                                <FiCalendar className="dog-reports-meta-icon" />
                                                Rozpoczęto: {formatDate(assignment.startDate)}
                                            </span>
                                            {assignment.endDate && (
                                                <span className={`dog-reports-date-badge ${assignmentHasEnded ? 'ended' : 'active'}`}>
                                                    <FiCalendar className="dog-reports-meta-icon" />
                                                    {assignmentHasEnded ? 'Zakończono:' : 'Zakończy się:'} {formatDate(assignment.endDate)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {!assignment.endDate && (
                                        <button
                                            className="reports-finish-assignment-btn"
                                            onClick={() => handleFinishAssignment(assignment)}
                                        >
                                            <FiX className="reports-btn-icon" />
                                            Zakończ
                                        </button>
                                    )}
                                </div>

                                <div className="dog-reports-submissions-timeline">
                                    {submissions.length === 0 ? (
                                        <div className="dog-reports-no-submissions">
                                            Brak dostępnych wypełnień dla tego raportu
                                        </div>
                                    ) : (
                                        <>
                                            <h4 className="dog-reports-timeline-title">Historia wypełnień:</h4>
                                            <div className="dog-reports-submissions-list">
                                                {submissions.map(submission => {
                                                    // Check if the due date is today or in the past
                                                    const dueDate = new Date(submission.dueDate);
                                                    dueDate.setHours(0, 0, 0, 0);

                                                    const today = new Date();
                                                    today.setHours(0, 0, 0, 0);

                                                    // Determine if submission is overdue (due date is today or in the past)
                                                    const isOverdue = dueDate <= today;

                                                    // A submission is pending if it's overdue, not completed, AND the assignment hasn't ended
                                                    const isPending = isOverdue && !submission.completed && !assignmentHasEnded;




                                                    // A submission is expired if the assignment has ended and it's not completed
                                                    const isExpired = assignmentHasEnded && !submission.completed;

                                                    return (
                                                        <div
                                                            key={submission.id}
                                                            className={`dog-reports-submission-item ${
                                                                submission.completed ? 'completed' :
                                                                    isExpired ? 'expired' :
                                                                        isPending ? 'pending' :
                                                                            'upcoming'
                                                            } ${activeSubmissionId === submission.id ? 'active' : ''}`}
                                                            onClick={(e) => handleSubmissionClick(e, submission.id)}
                                                        >
                                                            <div className="dog-reports-submission-header">
                                                                <div className="dog-reports-status-badge-small">
                                                                    {submission.completed ? (
                                                                        <>
                                                                            <FiCheckCircle className="dog-reports-status-icon" />
                                                                            <span>Wypełniony</span>
                                                                        </>
                                                                    ) : isExpired ? (
                                                                        <>
                                                                            <FiClock className="dog-reports-status-icon" />
                                                                            <span>Wygasł</span>
                                                                        </>
                                                                    ) : isPending ? (
                                                                        <>
                                                                            <FiClock className="dog-reports-status-icon" />
                                                                            <span>Oczekuje</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FiCalendar className="dog-reports-status-icon" />
                                                                            <span>Nadchodzący</span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                <div className="dog-reports-submission-date">
                                                                    <FiCalendar className="dog-reports-date-icon" />
                                                                    {formatDate(submission.dueDate)}
                                                                    {submission.completed && submission.submittedAt && (
                                                                        <span className="dog-reports-submitted-at">
                                                                            (Wypełniono: {formatDate(submission.submittedAt)})
                                                                        </span>
                                                                    )}
                                                                    {assignmentHasEnded && !submission.completed && (
                                                                        <span className="dog-reports-expired-note">
                                                                            (Zadanie zakończone: {formatDate(assignment.endDate)})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Submission details when clicked */}
                                                            {activeSubmissionId === submission.id && (
                                                                <div className="dog-reports-submission-details">
                                                                    <h4>Szczegóły raportu:</h4>

                                                                    {submission.completed ? (
                                                                        <div className="dog-reports-answers-list">
                                                                            {submission.answers.map((answer, index) => (
                                                                                <div key={answer.id} className="dog-reports-answer-item">
                                                                                    <div className="dog-reports-answer-question">
                                                                                        <span className="dog-reports-question-number">{index + 1}.</span>
                                                                                        <span className="dog-reports-question-text">{answer.questionText}</span>
                                                                                    </div>
                                                                                    <div className="dog-reports-answer-text">
                                                                                        {answer.answerText || <em>Brak odpowiedzi</em>}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : isExpired ? (
                                                                        <div className="dog-reports-expired-report">
                                                                            <p>Ten raport nie został wypełniony - zadanie zakończyło się {formatDate(assignment.endDate)}.</p>
                                                                        </div>
                                                                    ) : isPending ? (
                                                                        <div className="dog-reports-no-answers">
                                                                            Ten raport oczekuje na wypełnienie przez właściciela.
                                                                        </div>
                                                                    ) : (
                                                                        <div className="dog-reports-no-answers">
                                                                            Ten raport będzie dostępny do wypełnienia w przyszłości.
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Assign Report Modal */}
            {isAssignModalOpen && (
                <AssignReportModal
                    isOpen={isAssignModalOpen}
                    onClose={() => setIsAssignModalOpen(false)}
                    dogId={dogId}
                    templates={templates}
                    onAssigned={handleReportAssigned}
                />
            )}

            {/* Finish Assignment Modal */}
            {isFinishModalOpen && selectedAssignment && (
                <div className="reports-modal-overlay">
                    <div className="reports-modal dog-reports-finish-assignment-modal">
                        <div className="reports-modal-header">
                            <h2 className="reports-modal-title">Zakończ zadanie: {selectedAssignment.reportTemplateTitle}</h2>
                            <button
                                className="reports-close-btn"
                                onClick={() => {
                                    setIsFinishModalOpen(false);
                                    setSelectedAssignment(null);
                                    setFinishDate('');
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="reports-modal-content">
                            <p className="dog-reports-finish-description">
                                Wybierz datę zakończenia zadania. Po tej dacie właściciel nie będzie mógł wypełniać nowych raportów.
                            </p>

                            <div className="reports-input-group">
                                <label htmlFor="finishDate">Data zakończenia</label>
                                <input
                                    type="date"
                                    id="finishDate"
                                    value={finishDate}
                                    onChange={(e) => setFinishDate(e.target.value)}
                                    max={new Date().toISOString().split('T')[0]} // Cannot select future dates
                                    required
                                />
                            </div>

                            <div className="dog-reports-assignment-summary">
                                <h4>Podsumowanie zadania:</h4>
                                <div className="dog-reports-summary-item">
                                    <span className="dog-reports-summary-label">Szablon:</span>
                                    <span className="dog-reports-summary-value">{selectedAssignment.reportTemplateTitle}</span>
                                </div>
                                <div className="dog-reports-summary-item">
                                    <span className="dog-reports-summary-label">Data rozpoczęcia:</span>
                                    <span className="dog-reports-summary-value">{formatDate(selectedAssignment.startDate)}</span>
                                </div>
                                <div className="dog-reports-summary-item">
                                    <span className="dog-reports-summary-label">Powtarzanie:</span>
                                    <span className="dog-reports-summary-value">{getRecurrenceLabel(selectedAssignment.recurrence)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="reports-modal-footer">
                            <button
                                className="reports-cancel-btn"
                                onClick={() => {
                                    setIsFinishModalOpen(false);
                                    setSelectedAssignment(null);
                                    setFinishDate('');
                                }}
                                disabled={loading}
                            >
                                Anuluj
                            </button>
                            <button
                                className="dog-reports-finish-btn reports-save-btn"
                                onClick={handleSubmitFinishAssignment}
                                disabled={loading || !finishDate}
                            >
                                {loading ? 'Kończenie...' : 'Zakończ zadanie'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DogReportsTab;